import React, { useState, useEffect } from 'react';

interface Scores {
  trend?: number | null;
  momentum?: number | null;
  oi?: number | null;
  sentiment?: number | null;
  global?: number | null;
  weight?: number | null;
  total?: number | null;
}

interface SignalRow {
  label: string;
  key: keyof Scores;
  description: string;
}

const ROWS: SignalRow[] = [
  { label: 'Trend (SMA 20/50/200)', key: 'trend', description: 'Price vs SMA alignment' },
  { label: 'Momentum (RSI + MACD)', key: 'momentum', description: 'RSI level + MACD histogram' },
  { label: 'OI / PCR', key: 'oi', description: 'Put-Call Ratio signal' },
  { label: 'Grok / Twitter', key: 'sentiment', description: 'X/Twitter sentiment' },
  { label: 'Global Markets', key: 'global', description: 'SGX Nifty, Dow, USD/INR' },
  { label: 'Index Weight Analysis', key: 'weight', description: 'Weighted % of bullish Nifty 50 companies' },
];

function ScoreBadge({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) {
    return <span style={{ color: '#555', fontSize: '0.75rem' }}>—</span>;
  }
  const color = score > 0 ? '#26a65b' : score < 0 ? '#e74c3c' : '#f39c12';
  const label = score > 0 ? '▲ Bullish' : score < 0 ? '▼ Bearish' : '— Neutral';
  return (
    <span style={{
      color,
      fontWeight: 700,
      fontSize: '0.72rem',
      background: `${color}18`,
      padding: '2px 8px',
      borderRadius: 10,
    }}>
      {label}
    </span>
  );
}

interface Props {
  niftyScores?: Scores;
  bankniftyScores?: Scores;
  niftyPCR?: number;
  bankniftyPCR?: number;
}

export default function SignalScorecard({ niftyScores, bankniftyScores, niftyPCR, bankniftyPCR }: Props) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const nTotal = niftyScores?.total;
  const bTotal = bankniftyScores?.total;

  const totalColor = (t?: number | null) =>
    t !== undefined && t !== null ? (t >= 2 ? '#26a65b' : t <= -2 ? '#e74c3c' : '#f39c12') : '#555';

  return (
    <div style={{ background: '#0d0d14', border: '1px solid #222', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', background: '#111', padding: '8px 12px' }}>
        <span style={{ color: '#666', fontSize: '0.65rem', fontWeight: 700 }}>SIGNAL</span>
        <span style={{ color: '#4fc3f7', fontSize: '0.65rem', fontWeight: 700, textAlign: 'center' }}>NIFTY</span>
        <span style={{ color: '#ce93d8', fontSize: '0.65rem', fontWeight: 700, textAlign: 'center' }}>BANKNIFTY</span>
      </div>

      {ROWS.map((row, idx) => (
        <div key={row.key} style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr',
          padding: '7px 12px',
          borderBottom: idx < ROWS.length - 1 ? '1px solid #1a1a1a' : 'none',
          background: idx % 2 === 0 ? '#0d0d14' : '#0a0a10',
        }}>
          <div>
            <div style={{ color: '#bbb', fontSize: '0.72rem' }}>{row.label}</div>
            {!isMobile && <div style={{ color: '#444', fontSize: '0.6rem' }}>{row.description}</div>}
          </div>
          <div style={{ textAlign: 'center' }}>
            <ScoreBadge score={niftyScores?.[row.key]} />
            {row.key === 'oi' && niftyPCR !== undefined && (
              <div style={{ color: '#555', fontSize: '0.58rem', marginTop: 1 }}>PCR {niftyPCR?.toFixed(2)}</div>
            )}
          </div>
          <div style={{ textAlign: 'center' }}>
            <ScoreBadge score={bankniftyScores?.[row.key]} />
            {row.key === 'oi' && bankniftyPCR !== undefined && (
              <div style={{ color: '#555', fontSize: '0.58rem', marginTop: 1 }}>PCR {bankniftyPCR?.toFixed(2)}</div>
            )}
          </div>
        </div>
      ))}

      {/* Total row */}
      <div style={{
        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr',
        padding: '8px 12px', background: '#111', borderTop: '2px solid #222',
      }}>
        <div style={{ color: '#888', fontSize: '0.72rem', fontWeight: 700 }}>TOTAL SCORE</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: totalColor(nTotal), fontWeight: 800, fontSize: '0.85rem' }}>
            {nTotal !== null && nTotal !== undefined ? `${nTotal > 0 ? '+' : ''}${nTotal}` : '—'}
          </div>
          {nTotal !== null && nTotal !== undefined && (
            <div style={{ color: '#555', fontSize: '0.55rem' }}>
              {nTotal >= 2 ? 'bullish' : nTotal <= -2 ? 'bearish' : 'neutral'} · ±6 max
            </div>
          )}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: totalColor(bTotal), fontWeight: 800, fontSize: '0.85rem' }}>
            {bTotal !== null && bTotal !== undefined ? `${bTotal > 0 ? '+' : ''}${bTotal}` : '—'}
          </div>
          {bTotal !== null && bTotal !== undefined && (
            <div style={{ color: '#555', fontSize: '0.55rem' }}>
              {bTotal >= 2 ? 'bullish' : bTotal <= -2 ? 'bearish' : 'neutral'} · ±6 max
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
